<script setup>
/**
 * WhatsappBaileys.vue
 * Componente para criar e gerenciar conexao WhatsApp via Baileys microservice
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength, maxLength } from '@vuelidate/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import SettingsWhatsappBaileys from '../components/SettingsWhatsappBaileys.vue';
import BaileysAttributeItem from '../components/BaileysAttributeItem.vue';
import BaileysSettingsSections from 'dashboard/components-next/inbox/BaileysSettingsSections.vue';
import BaileysPairingCode from 'dashboard/components-next/inbox/BaileysPairingCode.vue';
import BaileysConnectMethodPicker from 'dashboard/components-next/inbox/BaileysConnectMethodPicker.vue';
import BaileysCredsImport from 'dashboard/components-next/inbox/BaileysCredsImport.vue';

// gateway: 'znew' quando a caixa vem da opção "WhatsApp Cortex ZNew" (gateway cortex-zaileys).
const props = defineProps({
  gateway: { type: String, default: null },
});

// Reactive data
const phoneNumber = ref('');
const channelName = ref('');
const step = ref('form'); // 'form' | 'creating' | 'qrcode' | 'connected' | 'settings'
const qrCodeData = ref(null);
const isLoadingQR = ref(false);
const isCheckingStatus = ref(false);
const isCreatingInstance = ref(false);
const createdInboxId = ref(null);
const errorMessage = ref(null);
const showSettings = ref(false);

// Configurações do Baileys (para serem passadas na criação)
const baileysConfig = ref({
  rejectCall: false,
  msgCall: '',
  receiveGroups: false, // true = ignorar grupos, false = receber grupos
  alwaysOnline: false,
  readMessages: false,
  importMessageHistory: false,
  historyDaysLimit: 7,
  readStatus: false,
  signMsg: false,
  importGroupHistory: false,
  reuseMedia: false,
});

// Como conectar: QR ou código digitado no celular. Código só existe no gateway ZNew.
const supportsPairingCode = computed(() => props.gateway === 'znew');
const connectMethod = ref('qr'); // 'qr' | 'code'

// Lista do Baileys legado (igual ao main; só o rótulo do readStatus corrigido). O ZNew usa as seções.
const legacyConfigAttributes = [
  { key: 'rejectCall', label: 'Rejeitar todas as chamadas recebidas', icon: '📵', type: 'checkbox', hasMessage: true },
  { key: 'receiveGroups', label: 'Ignorar todas as mensagens de grupos', icon: '👥', type: 'checkbox' },
  { key: 'alwaysOnline', label: 'Mantenha o WhatsApp sempre online', icon: '🟢', type: 'checkbox' },
  { key: 'readMessages', label: 'Marcar todas as mensagens como lidas', icon: '✅', type: 'checkbox' },
  { key: 'importMessageHistory', label: 'Importar histórico de mensagens ao escanear QR Code', icon: '🔄', type: 'checkbox' },
  { key: 'historyDaysLimit', label: 'Limite de dias para importação', icon: '📅', type: 'number', min: 1, max: 30, showIf: 'importMessageHistory' },
  { key: 'readStatus', label: 'Receber os status (stories) dos contatos', icon: '📸', type: 'checkbox' },
  { key: 'signMsg', label: 'Assinar mensagem com nome de usuário Cortexwoot', icon: '✍️', type: 'checkbox' },
];

// Composables
const store = useStore();
const router = useRouter();
const { t } = useI18n();

// Computed properties
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

// Computed para o inbox criado
const createdInbox = computed(() => {
  if (!createdInboxId.value) return null;
  return store.getters['inboxes/getInboxById'](createdInboxId.value);
});

// Get connection status from store
const connectionStatus = computed(() => {
  if (!createdInboxId.value) return null;
  return store.getters['inboxes/getBaileysConnectionStatus'](createdInboxId.value);
});

// Custom validator for channel name
const isPlainString = (value) => {
  if (!value) return false;
  return /^[a-zA-Z0-9]+$/.test(value);
};

// Validation rules
const rules = {
  channelName: {
    required,
    isPlainString: {
      $validator: isPlainString,
      $message: 'Nome deve conter apenas letras e numeros'
    }
  },
  phoneNumber: { required, isPhoneE164OrEmpty, minLength: minLength(13), maxLength: maxLength(15) },
};

// Vuelidate setup
const v$ = useVuelidate(rules, {
  channelName,
  phoneNumber,
});

// Status labels and colors
const statusConfig = computed(() => {
  const status = (connectionStatus.value?.status || '').toLowerCase();
  const configs = {
    'open': { label: 'Conectado', color: 'bg-green-500', icon: 'check-circle' },
    'connected': { label: 'Conectado', color: 'bg-green-500', icon: 'check-circle' },
    'connecting': { label: 'Conectando...', color: 'bg-yellow-500', icon: 'loader' },
    'close': { label: 'Desconectado', color: 'bg-red-500', icon: 'x-circle' },
    'disconnected': { label: 'Desconectado', color: 'bg-red-500', icon: 'x-circle' },
    'not_found': { label: 'Instancia nao encontrada', color: 'bg-orange-500', icon: 'alert' },
    'qr': { label: 'Aguardando QR Code', color: 'bg-blue-500', icon: 'qr-code' },
    'error': { label: 'Erro na conexao', color: 'bg-red-500', icon: 'x-circle' },
  };
  return configs[status] || { label: 'Aguardando...', color: 'bg-gray-500', icon: 'help-circle' };
});

const isConnected = computed(() => {
  const status = (connectionStatus.value?.status || '').toLowerCase();
  return status === 'open' || status === 'connected';
});

// Watch connection status to auto-transition to connected step
watch(isConnected, (connected) => {
  if (connected && step.value === 'qrcode') {
    step.value = 'connected';
    qrCodeData.value = null;
  }
});

// Debug watch para settings
watch(
  [showSettings, createdInboxId, createdInbox],
  ([settings, inboxId, inbox]) => {
    const allInboxes = store.getters['inboxes/getInboxes'];
    const inboxFromStore = allInboxes.find(i => i.id === inboxId);
  },
  { immediate: true }
);

// WebSocket event handlers
const handleBaileysConnected = (data) => {
  if (data.inbox_id !== createdInboxId.value) return;
  useAlert('WhatsApp conectado com sucesso!');
};

const handleBaileysDisconnected = (data) => {
  if (data.inbox_id !== createdInboxId.value) return;
  if (step.value === 'connected') {
    step.value = 'qrcode';
    qrCodeData.value = null;
  }
};

const handleBaileysQRGenerated = (data) => {
  if (Number(data.inbox_id) !== createdInboxId.value) return;
  if (connectMethod.value === 'qr') fetchQRCode();
};


// Methods
const createChannel = async () => {
  await v$.value.$validate();

  if (v$.value.$invalid) {
    return;
  }

  errorMessage.value = null;

  try {
    // Step 1: Create WhatsApp channel with baileys provider
    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: channelName.value,
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value,
        provider: 'baileys',
        provider_config: props.gateway ? { gateway: props.gateway } : {},
      },
    });

    createdInboxId.value = whatsappChannel.id;
    step.value = 'creating';
    isCreatingInstance.value = true;

    // Refresh inboxes no store para garantir que o inbox criado está disponível
    await store.dispatch('inboxes/get');

    // Step 2: Create instance in Baileys microservice com configurações
    try {
      await InboxesAPI.baileysCreateInstance(createdInboxId.value, {
        receive_groups: baileysConfig.value.receiveGroups, // true = ignorar grupos (inversão feita no backend)
        import_message_history: baileysConfig.value.importMessageHistory,
        history_days_limit: baileysConfig.value.historyDaysLimit,
        reject_call: baileysConfig.value.rejectCall,
        msg_call: baileysConfig.value.msgCall,
        always_online: baileysConfig.value.alwaysOnline,
        read_messages: baileysConfig.value.readMessages,
        read_status: baileysConfig.value.readStatus,
        sign_msg: baileysConfig.value.signMsg,
        import_group_history: baileysConfig.value.importGroupHistory,
        reuse_media: baileysConfig.value.reuseMedia,
      });
    } catch (instanceError) {
      console.warn('Instance may already exist or creation failed:', instanceError);
    }

    isCreatingInstance.value = false;
    step.value = 'qrcode';

    // Step 3: QR ou código, conforme a escolha
    if (connectMethod.value === 'qr') await fetchQRCode();

  } catch (error) {
    isCreatingInstance.value = false;
    errorMessage.value = error?.message || t('INBOX_MGMT.ADD.API_CHANNEL.API.ERROR_MESSAGE');
    useAlert(errorMessage.value);
  }
};

const fetchQRCode = async () => {
  if (!createdInboxId.value) return;

  isLoadingQR.value = true;
  errorMessage.value = null;

  try {
    const response = await InboxesAPI.baileysGetQRCode(createdInboxId.value);

    if (response.data?.success) {
      qrCodeData.value = response.data.qrcode?.base64;

      // Update store with status from QR response
      if (response.data.status) {
        store.dispatch('inboxes/setBaileysConnectionStatus', {
          inboxId: createdInboxId.value,
          status: response.data.status,
        });
      }
    } else {
      errorMessage.value = response.data?.error || 'Erro ao obter QR Code';
    }
  } catch (error) {
    console.error('Error fetching QR code:', error);
    await checkStatus();
  } finally {
    isLoadingQR.value = false;
  }
};

const useConnectMethod = async method => {
  connectMethod.value = method;
  if (method === 'qr') await fetchQRCode();
};

const setConnectionStatus = status =>
  store.dispatch('inboxes/setBaileysConnectionStatus', {
    inboxId: createdInboxId.value,
    status,
  });

const checkStatus = async () => {
  if (!createdInboxId.value) return;

  isCheckingStatus.value = true;
  errorMessage.value = null;

  try {
    const response = await InboxesAPI.baileysGetStatus(createdInboxId.value);

    if (response.data?.success) {
      store.dispatch('inboxes/setBaileysConnectionStatus', {
        inboxId: createdInboxId.value,
        status: response.data.data,
      });
    }
  } catch (error) {
    console.error('Error checking status:', error);
    store.dispatch('inboxes/setBaileysConnectionStatus', {
      inboxId: createdInboxId.value,
      status: { status: 'error', error: error.message },
    });
  } finally {
    isCheckingStatus.value = false;
  }
};

const disconnectInstance = async () => {
  if (!createdInboxId.value) return;

  try {
    await InboxesAPI.baileysDisconnect(createdInboxId.value);
    store.dispatch('inboxes/setBaileysConnectionStatus', {
      inboxId: createdInboxId.value,
      status: { status: 'disconnected' },
    });
    useAlert('Instancia desconectada com sucesso');
    step.value = 'qrcode';
    qrCodeData.value = null;
    await fetchQRCode();
  } catch (error) {
    useAlert(error?.message || 'Erro ao desconectar instancia');
  }
};

const finishSetup = () => {
  router.replace({
    name: 'settings_inbox_finish',
    params: { page: 'new', inbox_id: createdInboxId.value },
  });
};

// Lifecycle hooks
onMounted(() => {
  emitter.on(BUS_EVENTS.BAILEYS_CONNECTED, handleBaileysConnected);
  emitter.on(BUS_EVENTS.BAILEYS_DISCONNECTED, handleBaileysDisconnected);
  emitter.on(BUS_EVENTS.BAILEYS_QR_GENERATED, handleBaileysQRGenerated);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.BAILEYS_CONNECTED, handleBaileysConnected);
  emitter.off(BUS_EVENTS.BAILEYS_DISCONNECTED, handleBaileysDisconnected);
  emitter.off(BUS_EVENTS.BAILEYS_QR_GENERATED, handleBaileysQRGenerated);
});
</script>

<template>
  <div class="w-full max-w-4xl mx-auto">
    <!-- Step 1: Form to create inbox -->
    <div v-if="step === 'form'">
      <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
        <div class="flex-shrink-0 flex-grow-0 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label :class="{ error: v$.channelName.$error }">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
            <input
              v-model="channelName"
              type="text"
              class="w-full"
              :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
              @blur="v$.channelName.$touch"
            />
            <span v-if="v$.channelName.$error" class="message">
              Nome deve conter apenas letras e numeros (sem espacos ou acentos)
            </span>
          </label>

          <label :class="{ error: v$.phoneNumber.$error }">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
            <input
              v-model="phoneNumber"
              type="text"
              class="w-full"
              :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
              @blur="v$.phoneNumber.$touch"
            />
            <span v-if="v$.phoneNumber.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
            </span>
          </label>
        </div>

        <!-- Como conectar: só no ZNew (o gateway legado não tem código de pareamento) -->
        <BaileysConnectMethodPicker
          v-if="supportsPairingCode"
          v-model="connectMethod"
          class="mt-2 mb-8"
        />

        <!-- Configurações do WhatsApp, em seções (mesma lista das configurações da caixa) -->
        <BaileysSettingsSections
          v-if="supportsPairingCode"
          :settings="baileysConfig"
          @update="({ key, value }) => (baileysConfig[key] = value)"
        />

        <!-- Configurações do Baileys legado (como no main) -->
        <div v-else class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Configuracoes do WhatsApp
          </h3>

          <div class="space-y-1">
            <template v-for="attr in legacyConfigAttributes" :key="attr.key">
              <!-- Mostrar apenas se nao tem condicao ou se a condicao e verdadeira -->
              <BaileysAttributeItem
                v-if="!attr.showIf || baileysConfig[attr.showIf]"
                :attribute="attr"
                v-model="baileysConfig[attr.key]"
                v-model:msg-call-value="baileysConfig.msgCall"
              />
            </template>
          </div>
        </div>

        <!-- Error message -->
        <p
          v-if="errorMessage && supportsPairingCode"
          role="alert"
          class="mt-6 mb-0 flex items-start gap-2 p-3 rounded-lg bg-n-ruby-2 outline outline-1 outline-n-ruby-6 text-sm text-n-ruby-11"
        >
          <span
            class="i-lucide-circle-alert size-4 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          {{ errorMessage }}
        </p>
        <div v-else-if="errorMessage" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p class="text-sm text-red-600 dark:text-red-400">{{ errorMessage }}</p>
        </div>

        <div class="w-full" :class="supportsPairingCode ? 'mt-8' : 'mt-4'">
          <NextButton
            :is-loading="uiFlags.isCreating"
            type="submit"
            solid
            blue
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
          />
        </div>
      </form>
    </div>

    <!-- Step 1.5: Creating instance -->
    <div v-else-if="step === 'creating'" class="text-center py-12">
      <div class="animate-spin w-12 h-12 mx-auto mb-4 border-4 border-woot-500 border-t-transparent rounded-full"></div>
      <h2 class="text-xl font-semibold text-slate-12 mb-2">
        Criando instancia Baileys...
      </h2>
      <p class="text-sm text-slate-11">
        Aguarde enquanto configuramos sua conexao WhatsApp
      </p>
    </div>

    <!-- Step 2: QR Code scanning -->
    <div v-else-if="step === 'qrcode'" class="text-center">
      <template v-if="connectMethod === 'qr'">
      <div class="mb-6">
        <h2 class="text-xl font-semibold text-slate-12 mb-2">
          Escanear QR Code
        </h2>
        <p class="text-sm text-slate-11">
          Abra o WhatsApp no seu celular e escaneie o QR Code abaixo para conectar
        </p>
      </div>

      <!-- Status indicator -->
      <div class="mb-4 flex justify-center items-center gap-2">
        <span
          class="w-3 h-3 rounded-full animate-pulse"
          :class="statusConfig.color"
        ></span>
        <span class="text-sm text-slate-11">{{ statusConfig.label }}</span>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p class="text-sm text-red-600 dark:text-red-400">{{ errorMessage }}</p>
      </div>

      <!-- QR Code display -->
      <div class="flex justify-center mb-6">
        <div
          class="p-4 bg-white rounded-lg shadow-lg"
          :class="{ 'animate-pulse': isLoadingQR }"
        >
          <img
            v-if="qrCodeData && qrCodeData.startsWith('data:')"
            :src="qrCodeData"
            alt="QR Code WhatsApp"
            class="w-64 h-64"
          />
          <div
            v-else-if="qrCodeData"
            class="w-64 h-64 flex items-center justify-center bg-gray-100 rounded"
          >
            <span class="text-xs text-gray-500 break-all p-2">{{ qrCodeData }}</span>
          </div>
          <div
            v-else
            class="w-64 h-64 flex items-center justify-center bg-gray-100 rounded"
          >
            <span v-if="isLoadingQR" class="text-gray-500">Carregando QR Code...</span>
            <span v-else class="text-gray-500">Clique em "Atualizar QR Code"</span>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-center gap-4">
        <NextButton
          :is-loading="isLoadingQR"
          @click="fetchQRCode"
          solid
          :label="'Atualizar QR Code'"
        />
        <NextButton
          :is-loading="isCheckingStatus"
          @click="checkStatus"
          :label="'Verificar Status'"
        />
      </div>

      <!-- Instructions -->
      <div class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-left">
        <h3 class="font-medium text-slate-12 mb-2">Como conectar:</h3>
        <ol class="list-decimal list-inside text-sm text-slate-11 space-y-1">
          <li>Abra o WhatsApp no seu celular</li>
          <li>Toque em Menu ou Configuracoes e selecione "Aparelhos conectados"</li>
          <li>Toque em "Conectar um aparelho"</li>
          <li>Aponte seu celular para esta tela para escanear o QR Code</li>
        </ol>
      </div>

        <p v-if="supportsPairingCode" class="mt-4 text-sm">
          <button
            type="button"
            class="text-n-blue-11 underline"
            @click="useConnectMethod('code')"
          >
            {{ t('INBOX_MGMT.BAILEYS_PAIRING.USE_CODE') }}
          </button>
        </p>
      </template>

      <!-- Conexão por código ("Conectar com número de telefone" no celular) -->
      <BaileysPairingCode
        v-else
        :inbox-id="createdInboxId"
        :phone-number="phoneNumber"
        @status="setConnectionStatus"
        @use-qr="useConnectMethod('qr')"
      />

      <!-- Import de credenciais (contorno da passkey) -->
      <div class="mt-4">
        <BaileysCredsImport
          v-if="createdInboxId"
          :inbox-id="createdInboxId"
          @imported="checkStatus"
        />
      </div>
    </div>

    <!-- Step 3: Connected -->
    <div v-else-if="step === 'connected'">
      <!-- Toggle between connection info and settings -->
      <div v-if="!showSettings" class="text-center">
        <div class="mb-6">
          <div class="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-slate-12 mb-2">
            WhatsApp Conectado!
          </h2>
          <p class="text-sm text-slate-11">
            Sua instancia do WhatsApp foi conectada com sucesso via Baileys
          </p>
        </div>

        <!-- Connection info -->
        <div v-if="connectionStatus" class="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-left">
          <h3 class="font-medium text-slate-12 mb-2">Informacoes da conexao:</h3>
          <div class="text-sm text-slate-11 space-y-1">
            <p><span class="font-medium">Status:</span> {{ statusConfig.label }}</p>
            <p v-if="connectionStatus.phone_number || connectionStatus.phoneNumber">
              <span class="font-medium">Numero:</span> {{ connectionStatus.phone_number || connectionStatus.phoneNumber }}
            </p>
            <p><span class="font-medium">Inbox:</span> {{ channelName }}</p>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-center gap-4 flex-wrap">
          <NextButton
            @click="showSettings = true"
            outline
            :label="'Configurar WhatsApp'"
          />
          <NextButton
            @click="finishSetup"
            solid
            blue
            :label="'Concluir Configuracao'"
          />
          <NextButton
            @click="disconnectInstance"
            variant="destructive"
            :label="'Desconectar'"
          />
        </div>
      </div>

      <!-- Settings view -->
      <div v-else>
        <div class="mb-4 flex items-center justify-between">
          <NextButton
            @click="showSettings = false"
            outline
            slate
            size="sm"
            :label="'Voltar'"
          />
          <h2 class="text-lg font-semibold text-slate-12">
            Configuracoes do WhatsApp
          </h2>
          <div class="w-16"></div>
        </div>

        <SettingsWhatsappBaileys
          v-if="createdInbox"
          :inbox="createdInbox"
        />

        <div class="mt-6 flex justify-center gap-4">
          <NextButton
            @click="showSettings = false; finishSetup()"
            solid
            blue
            :label="'Salvar e Concluir'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Focus states */
input:focus {
  @apply outline-2 outline-offset-2 outline-woot-500;
}
</style>
